import { computed, inject, type ComputedRef } from 'vue'
import { merge, mergeWith } from 'lodash-es'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCrmShape from '@canvas-builder/composables/useCrmShape'
import type { User } from '@openapi'

export default function useCanvasContext() {
  const { activeCanvas, isPrintDisplayMode, builderCache } = useCanvas()
  const { crmShape, getDefaultCrmShape } = useCrmShape()

  const myUser = inject<ComputedRef<User | null>>(
    'myUser',
    computed(() => null),
  )

  const sectionListSectionInfo = inject<
    ComputedRef<{
      usedInSectionId: string
      sectionIdx: number
      sectionListId: string
      printModeIdx?: number
    } | null>
  >(
    'sectionListSectionInfo',
    computed(() => null),
  )

  const getSectionCtx = () => {
    const sectionInfo = sectionListSectionInfo.value
    if (!activeCanvas.value?.context) {
      return {}
    }
    const currentCtx = activeCanvas.value?.context

    // If there's no section info, just return the base context
    if (!sectionInfo) {
      return activeCanvas.value.context
    }
    // Create a section-specific override key
    // In print mode, use the sequential printModeIdx if available
    const idxToUse =
      isPrintDisplayMode.value && sectionInfo.printModeIdx !== undefined
        ? sectionInfo.printModeIdx
        : sectionInfo.sectionIdx
    const sectionOverrideKey = `ctx_overwrite_${sectionInfo.sectionListId}_${idxToUse}`
    // Check if we have section overrides
    const hasOverrides = currentCtx && typeof currentCtx === 'object' && currentCtx[sectionOverrideKey]
    if (!hasOverrides) {
      // Add section metadata to context but don't override anything
      return activeCanvas.value.context
    }

    if (builderCache.value[sectionOverrideKey]) {
      return builderCache.value[sectionOverrideKey]
    }

    // Get the section-specific override context
    const sectionOverrides = currentCtx[sectionOverrideKey]
    const mergedCtx = merge({}, currentCtx, sectionOverrides)
    builderCache.value[sectionOverrideKey] = mergedCtx

    return mergedCtx
  }

  const fullContext = computed(() => {
    const mergedCtx = getSectionCtx()
    // Always include default CRM shape for CDynamicValueTreeSelect field availability
    // regardless of enable_default_crm_shape flag
    const defaultShape = getDefaultCrmShape({
      myUser: myUser.value,
      showDefault: true,
    })
    // Deep merge: defaultShape + crmShape provides structure/available fields, context provides real data
    // Context is merged last so real values overwrite schema nulls
    return mergeWith({}, defaultShape, crmShape.value ?? {}, mergedCtx, (_objValue, srcValue) => {
      if (Array.isArray(srcValue)) {
        return srcValue
      }
      return undefined
    })
  })

  return {
    fullContext,
  }
}
